import { useDistricts } from '../../hooks/useDistricts';
import { LoadingState } from '../shared/LoadingStates';
import RaceCard from '../shared/RaceCard';

export default function RunTab() {
  const { data: openSeats, isLoading } = useDistricts({ openSeats: true });

  return (
    <div className="h-full overflow-y-auto" id="run-panel" role="tabpanel">
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-6">
        <h2 className="text-2xl font-bold mb-2">Run for Office</h2>
        <p className="text-blue-100">
          Explore open seats and recruitment opportunities for 2026
        </p>
      </div>

      {isLoading ? (
        <LoadingState />
      ) : (
        <div className="p-4 space-y-6">
          {/* Open Seats */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-2xl">🔓</span>
              <h3 className="text-xl font-bold">Open Seats</h3>
              {openSeats && openSeats.length > 0 && (
                <span className="bg-open text-white text-sm px-2 py-1 rounded-full font-semibold">
                  {openSeats.length}
                </span>
              )}
            </div>

            {openSeats && Array.isArray(openSeats) && openSeats.length > 0 ? (
              <div className="space-y-3">
                {openSeats.map(seat => (
                  <RaceCard key={seat.id || seat.district_code} district={seat} />
                ))}
              </div>
            ) : (
              <div className="bg-slate-800 rounded-lg p-6 text-center border border-slate-700">
                <div className="text-4xl mb-2">📋</div>
                <div className="text-white font-medium">No open seats currently listed</div>
                <div className="text-sm text-slate-400 mt-1">
                  Check back as filing deadlines approach
                </div>
              </div>
            )}
          </section>

          {/* Resources */}
          <section className="bg-slate-800/50 border-2 border-blue-500/30 rounded-lg p-6">
            <h3 className="text-lg font-bold text-accent-blue mb-4 flex items-center gap-2">
              <span>📚</span> Resources for Candidates
            </h3>
            <div className="space-y-3 text-sm">
              <ResourceLink
                icon="🏛️"
                title="DCCC Recruitment"
                description="Democratic Congressional Campaign Committee"
                href="https://dccc.org"
              />
              <ResourceLink
                icon="📋"
                title="FEC Candidate Guide"
                description="Official filing requirements and deadlines"
                href="https://www.fec.gov/help-candidates-and-committees/registering-candidate/"
              />
              <ResourceLink
                icon="💰"
                title="ActBlue"
                description="Online fundraising platform for Democrats"
                href="https://secure.actblue.com"
              />
              <ResourceLink
                icon="🎓"
                title="Campaign Training"
                description="Wellstone Action training programs"
                href="https://wellstone.org"
              />
            </div>
          </section>

          {/* Call to Action */}
          <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-lg p-6 text-center">
            <h3 className="text-xl font-bold mb-2">Ready to Make a Difference?</h3>
            <p className="text-blue-100 mb-4">
              Every seat matters in the fight for the House majority
            </p>
            <a
              href="https://dccc.org/get-involved/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-6 py-3 bg-white text-blue-600 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
            >
              Contact DCCC →
            </a>
          </section>
        </div>
      )}
    </div>
  );
}

function ResourceLink({ icon, title, description, href }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-start gap-3 p-3 bg-slate-700/50 rounded-lg hover:bg-slate-700 border border-slate-600/50 hover:border-blue-500/50 transition-all"
    >
      <span className="text-2xl">{icon}</span>
      <div className="flex-1">
        <div className="font-semibold text-white">{title}</div>
        <div className="text-slate-400 text-xs">{description}</div>
      </div>
      <span className="text-accent-blue">→</span>
    </a>
  );
}
